import { CodingDraftPreviewContext, useCodingDraftPreview } from "../../../meddra/hooks/useCodingDraftPreview";
import { JsonForms } from "@jsonforms/react";
import { useMemo } from "react";
import type { JsonSchema, UISchemaElement } from "@jsonforms/core";
import type { ErrorObject } from "ajv";
import { createCatalogRuleAjv } from "../../utils/catalogAjv";
import { useCatalogValidationI18n } from "../../hooks/useCatalogValidationI18n";
import { resolveCatalogError } from "../../utils/catalogValidationMessages";
import { CaseReviewContext } from "../../hooks/useCaseFieldReview";
import { caseReviewRenderers } from "../../renderers/CaseReviewRenderers";
import type { CaseReviewContextValue, CaseFieldValues } from "../../model/caseReview";
import { projectScalarUiSchema } from "../../utils/caseReviewLayout";
import { conditionalRequiredFields } from "../../utils/catalogConditionalRules";

interface Props {
  schema: JsonSchema; uischema: UISchemaElement; values: CaseFieldValues;
  context: CaseReviewContextValue;
  onValidation: (errors: ErrorObject[]) => void;
  enabled?: boolean;
}
/** One JSON Forms configuration for every scalar case review section. */
export function CaseFieldReviewTable({ schema, uischema, values, context, onValidation, enabled = true }: Props) {
  const codingFieldId = String((schema.properties?.meddra_coding as Record<string, unknown> | undefined)?.['x-field-id'] ?? '');
  const codingPreview = useCodingDraftPreview(values.meddra_coding, codingFieldId);
  // Server-resolved schemas can reuse $id with new content; do not share an AJV registry across them.
  const ajv = useMemo(() => createCatalogRuleAjv(), [schema]);
  const i18n = useCatalogValidationI18n(schema, values);
  // JSON Forms reloads controlled data when layout identity changes. Keep it stable during edits.
  const slotScopes = useMemo(() => Object.keys(context.childSlots ?? {}), [context.childSlots]);
  const layout = useMemo(() => projectScalarUiSchema(uischema, schema, slotScopes), [uischema, schema, slotScopes]);
  const requiredFields = useMemo(() => conditionalRequiredFields(schema, values, ajv), [schema, values, ajv]);
  return <CaseReviewContext.Provider value={{ ...context, requiredFields }}>
    <CodingDraftPreviewContext.Provider value={codingPreview}>
    <JsonForms schema={schema} uischema={layout} data={values} ajv={ajv} i18n={i18n} renderers={caseReviewRenderers}
      onChange={({ data, errors }) => onValidation((errors ?? []).filter((error) => error.keyword !== "if")
        .map((error) => ({ ...error, ...resolveCatalogError(schema, data, error) })))} readonly={!enabled} />
    </CodingDraftPreviewContext.Provider>
  </CaseReviewContext.Provider>;
}
